// src/pages/Slice.jsx
import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Link } from "react-router-dom";
import { runSample } from "../api/slice";
import LoafBadge from "../components/LoafBadge";

export default function Slice() {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleRunSample = async () => {
    setIsRunning(true);
    setError(null);
    setResult(null);

    try {
      const sampleResult = await runSample("slice");
      setResult(sampleResult);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) => theme.palette.background.gradient,
        backgroundAttachment: "fixed",
        color: "#f3f6f9",
        py: { xs: 6, md: 10 },
        px: 2,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1280, mx: "auto" }}>
        <Stack spacing={4} alignItems="center">
          <Box textAlign="center">
            <Typography
              variant="h2"
              sx={{ fontWeight: 900, letterSpacing: 0.3, mb: 2 }}
            >
              LoafSlice
            </Typography>
            <Typography
              variant="h5"
              sx={{ opacity: 0.85, maxWidth: 600, mb: 3 }}
            >
              One-click evals with a glass report. Pass/Fail for PRs.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <LoafBadge badgeContent="2s" size="small">
                <Button variant="outlined" size="small">
                  Speed
                </Button>
              </LoafBadge>
              <LoafBadge badgeContent="98%" size="small">
                <Button variant="outlined" size="small">
                  Pass Rate
                </Button>
              </LoafBadge>
            </Stack>
          </Box>

          <Card
            sx={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "16px",
              maxWidth: 600,
              width: "100%",
            }}
          >
            <CardContent sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="h6" gutterBottom color="white">
                Run Sample Evaluation
              </Typography>
              <Typography
                variant="body2"
                color="rgba(255, 255, 255, 0.8)"
                paragraph
              >
                Execute a sample evaluation to see LoafSlice in action with
                real-time results.
              </Typography>

              <Button
                variant="contained"
                onClick={handleRunSample}
                disabled={isRunning}
                sx={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                  },
                  mb: 3,
                }}
                fullWidth
              >
                {isRunning ? (
                  <>
                    <CircularProgress
                      size={16}
                      sx={{ mr: 1, color: "white" }}
                    />
                    Running Sample...
                  </>
                ) : (
                  "Run Sample"
                )}
              </Button>

              {result && (
                <Alert
                  severity="success"
                  sx={{
                    background: "rgba(76, 175, 80, 0.2)",
                    color: "white",
                    "& .MuiAlert-icon": { color: "#51c4a0" },
                  }}
                >
                  <Typography variant="body2">
                    Sample executed successfully!
                    {result.message && ` ${result.message}`}
                  </Typography>
                </Alert>
              )}

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    background: "rgba(244, 67, 54, 0.2)",
                    color: "white",
                    "& .MuiAlert-icon": { color: "#f44336" },
                  }}
                >
                  <Typography variant="body2">Error: {error}</Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Button
            variant="outlined"
            component={Link}
            to="/"
            sx={{ borderRadius: 999, px: 3 }}
          >
            Back to Home
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
